import React, { useEffect, useRef, useState } from "react"
import { Reorder, useDragControls } from "framer-motion"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import useDashboardTexts from "./useDashboardTexts"
import { XCircleIcon, EyeIcon, EyeSlashIcon, PlusIcon, TrashIcon, HashtagIcon, ChartBarIcon, TableCellsIcon, Bars3BottomLeftIcon } from "@heroicons/react/24/outline"

const ICONS = { metric: HashtagIcon, chart: ChartBarIcon, table: TableCellsIcon, segments: Bars3BottomLeftIcon }
const TYPE = { metric: "Number", chart: "Chart", table: "Table", segments: "Segments" }

/** The nearest box that scrolls: the drawer's list, which a drag near its edge moves. */
const scrollerOf = (el) => {
  for (let n = el?.parentElement; n; n = n.parentElement) {
    const { overflowY } = getComputedStyle(n)
    if (/(auto|scroll)/.test(overflowY) && n.scrollHeight > n.clientHeight) return n
  }
  return null
}

/**
 * One card of the list. Its grip starts the drag — with a finger, a pen or a
 * mouse — and the card lifts while it is carried (a little larger, with a
 * shadow); near the drawer's top or bottom edge the list scrolls under it.
 */
function CardRow({ card, label, t, onEdit, onToggle, onRemove, onDragEnd }) {
  const controls = useDragControls()
  const rowRef = useRef(null)
  const Icon = ICONS[card.type] || HashtagIcon

  const autoScroll = (event, info) => {
    const scroller = scrollerOf(rowRef.current)
    if (!scroller) return
    const box = scroller.getBoundingClientRect()
    const y = info.point.y - window.scrollY
    const edge = 60
    if (y < box.top + edge) scroller.scrollTop -= Math.ceil((box.top + edge - y) / 4)
    else if (y > box.bottom - edge) scroller.scrollTop += Math.ceil((y - (box.bottom - edge)) / 4)
  }

  return (
    <Reorder.Item
      ref={rowRef}
      value={card}
      dragListener={false}
      dragControls={controls}
      onDrag={autoScroll}
      onDragEnd={onDragEnd}
      whileDrag={{ scale: 1.03, boxShadow: "0 12px 28px rgba(0,0,0,0.18)", zIndex: 30 }}
      className="relative flex items-center gap-2 rounded-lg border border-admin_bordered bg-admin_paper px-2 py-2 list-none"
      data-testid="customize-card"
    >
      {/* the grip: touch-action none, so a finger drags the card instead of scrolling the page */}
      <span
        onPointerDown={(e) => {
          e.preventDefault()
          controls.start(e)
        }}
        className="cursor-grab active:cursor-grabbing px-1.5 py-2 -my-2 text-admin_text_secondary select-none touch-none"
        title={t`Drag to reorder`}
        data-testid="customize-card-grip"
      >
        ⋮⋮
      </span>
      <Icon className="w-4 h-4 shrink-0 text-admin_text_secondary" />
      {/* the name opens the card's editor */}
      <button type="button" onClick={() => onEdit(card)} className={`flex-1 min-w-0 text-left group/name ${card.hidden ? "opacity-50" : ""}`} title={t`Edit card`} data-testid="customize-card-edit">
        <span className="block truncate text-sm text-admin_text group-hover/name:text-admin_primary group-hover/name:underline">{label}</span>
        <span className="block text-[11px] text-admin_text_secondary">
          {t(TYPE[card.type])} · {card.size === "l" ? t`wide` : card.size === "m" ? t`medium` : t`small`}
          {card.hidden ? ` · ${t`hidden`}` : ""}
        </span>
      </button>
      {/* shown or hidden on the dashboard, without taking it away */}
      <button type="button" onClick={() => onToggle(card)} className="p-1 rounded text-admin_text_secondary hover:text-admin_primary" title={card.hidden ? t`Show on the dashboard` : t`Hide from the dashboard`} data-testid="customize-card-visibility">
        {card.hidden ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
      </button>
      <button type="button" onClick={() => onRemove(card)} className="p-1 rounded text-admin_text_secondary hover:text-admin_error" title={t`Remove`}>
        <XCircleIcon className="w-5 h-5" />
      </button>
    </Reorder.Item>
  )
}

/**
 * The dashboard's cards as a list, the way Google Analytics' "Customize
 * report" has them: drag by the grip to reorder (touch too), edit, hide,
 * remove, add; the dashboard's name, and deleting it.
 */
export default function CustomizePanel({ isOpen, onClose, dashboard, catalog, onRename, onReorder, onEdit, onToggle, onRemove, onAdd, onDeleteDashboard, canDelete }) {
  const t = useDashboardTexts()
  const cards = dashboard?.cards || []
  // the order while a card is carried; kept when it is let go
  const [order, setOrder] = useState(cards)
  useEffect(() => setOrder(cards), [dashboard?.cards])
  const labelOf = (card) => card.title || t(card.type === "table" ? catalog?.tables?.find((x) => x.id === card.table)?.label : catalog?.metrics?.find((m) => m.id === card.metric)?.label) || card.metric || card.table

  const commit = () => {
    if (order.map((c) => c.id).join() !== cards.map((c) => c.id).join()) onReorder(order)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="admin_drawer_right" title={t`Customize dashboard`} closeOnEscape className="max-w-[440px]">
      <div className="flex flex-col gap-5 p-4" data-testid="dashboard-customize">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-admin_text_secondary">{t`Name`}</span>
          <input
            // the starter's name has a translation; the one typed in stays as typed
            defaultValue={t(dashboard?.name)}
            key={dashboard?._id}
            onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== t(dashboard?.name) && onRename(e.target.value.trim())}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className="h-10 px-3 rounded-lg border border-admin_bordered bg-admin_paper text-sm text-admin_text outline-none focus:border-admin_primary"
            data-testid="dashboard-name"
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-admin_text_secondary">{t("Cards (%s)", cards.length)}</span>
          <button type="button" onClick={onAdd} className="flex items-center justify-center gap-1.5 h-11 rounded-lg border border-dashed border-admin_bordered text-sm font-semibold text-admin_primary hover:border-admin_primary" data-testid="customize-add-card">
            <PlusIcon className="w-4 h-4" />
            {t`Add card`}
          </button>
          <Reorder.Group axis="y" values={order} onReorder={setOrder} className="flex flex-col gap-2 m-0 p-0" data-testid="customize-list">
            {order.map((card) => (
              <CardRow key={card.id} card={card} label={labelOf(card)} t={t} onEdit={onEdit} onToggle={onToggle} onRemove={onRemove} onDragEnd={commit} />
            ))}
          </Reorder.Group>
        </div>

        {canDelete && (
          <Button variant="admin_secondary" className="self-center text-admin_error border-admin_error/50" onClick={onDeleteDashboard}>
            <span className="flex items-center gap-1"><TrashIcon className="w-4 h-4" />{t`Delete this dashboard`}</span>
          </Button>
        )}
      </div>
    </Modal>
  )
}
